import { useProjectStore, useReplaceStore, useTrackStore } from '@ve-pro/store'
import {
  ProjectConfig,
  Speaker,
  SpeakerCollection,
  SpeakerLanguageVoice,
  SettledSpeakerVoice,
  SpeakerVoice,
} from '@ve-pro/api/project/type'
import { isSegmentSubtitle } from '@ve-pro/store/tracks/segment/segment-subtitle'
import _ from 'lodash'
import { SegmentCollection, SegmentText } from '@ve-pro/types/track.type'
import { getSpeechVoiceList } from '@/api/ai/speech'
import { addSpeakerVoice } from '@ve-pro/replace/multi-languages/languages-detail/language-detail'
import {
  getCustomSpeaker,
  getSpeakerDefaultVoice,
  updateSpeakerVoice,
  isSpeakerUserUpload,
  updateSpeakerVoiceByLab,
} from '@ve-pro/store/project/config'
import { addCustomSpeaker } from '@ve-pro/store/tracks/segment/segment-subtitle'
import Vue from 'vue'
import { t } from '@ve-pro/ve/lang'
import type { TranslateResult } from 'vue-i18n'
import type { SpeechVoiceModel } from '@/api/ai/index.type'
import { cloneDeep } from 'lodash-es'
import { Gender } from '@com/components/speaker-gender/utils'
import { type ComputedRef, ref } from 'vue'
import { retryPromise } from '@ve-pro/ve/utils/promise'
import { getClosestUnLangSegment } from '../project/languages'
import { VoiceItem } from '@pt/api/ai/index.type'
import { Message } from 'element-ui'

export const SPEAKER_VOICE_UNSET_LABLE = '-'
export const SPEAKER_VOICE_CLONE_LABLE = t(
  'videoTemplate.detail.table.action.clone',
)
export const SPEAKER_VOICE_UNUSE_LABLE = t(
  'videoTemplate.detail.table.action.nodub',
)

const requredSpeakerVoice = (msg: any) => {
  console.error(`speaker voice must be defined`, msg)
}

type SpeakerValue = keyof SpeakerCollection

interface CreateSpeakerVoice {
  /** 该值默认继承voice下的性别 */
  selectedGender?: SpeakerVoice['selectedGender']
  voice?: SpeechVoiceModel
  clone: boolean
}

export type CreateSpeaker = Pick<
  CreateSpeakerVoice,
  'clone' | 'selectedGender'
> & {
  languages?: string[]
  unsetDefualtLanguage?: boolean
  voices: SpeechVoiceModel[]
}

/**
 * 获取当前激活状态的 speaker
 */
export function getAllActiveSpeakers(
  subtitles?: SegmentCollection[],
): ProjectConfig['speaker'] {
  const trackStore = useTrackStore()
  return getSpeakersBySegments(subtitles ?? trackStore.segments)
}

export function getSpeakersBySegments(
  segments: SegmentCollection[],
): ProjectConfig['speaker'] {
  const speakerCollections: ProjectConfig['speaker'] = {}
  const projectStore = useProjectStore()

  segments.filter(isSegmentSubtitle).forEach((item) => {
    const speaker = item.speaker
    if (!speaker) {
      return []
    }

    const key = projectStore.isReplaceProject
      ? item.source_segment_id
      : Number(String(getClosestUnLangSegment(item)?.source).split('-')[1] ?? 0)

    const targetSpeakerVoice = projectStore.configs.speaker[key]?.[speaker]
    if (targetSpeakerVoice) {
      let leftValue = speakerCollections[key]
      if (!leftValue) {
        leftValue = speakerCollections[key] = {}
      }

      leftValue[speaker] = projectStore.configs.speaker[key]?.[speaker] as any
    }
  })

  return speakerCollections
}

export function getActiveCloneLength(
  activeSpeaker: ProjectConfig['speaker'],
  languages?: string[],
): number {
  console.log(activeSpeaker, '角色')
  let length = 0
  // 未配置语言，默认选中所有语言
  // 配置语言，只过滤目标语言
  const checkIsTargetLanguage = (lang: string) => {
    return (
      !languages ||
      languages.length === 0 ||
      languages.some((item) => item === lang)
    )
  }
  _.each(activeSpeaker, (item) => {
    _.each(item, (el) => {
      if (
        Object.keys(el).some((key) => {
          return (
            el[key] &&
            el[key].clone &&
            checkIsTargetLanguage(key) &&
            !isSpeakerUserUpload(el[key])
          )
        })
      ) {
        length++
      }
    })
  })
  return length
}

export function getAllActiveCloneLength(
  langs: string[],
  subtitles?: SegmentCollection[],
): number {
  const allActiveSpeaker = getAllActiveSpeakers(subtitles)
  return getActiveCloneLength(allActiveSpeaker, langs)
}

export function createSpeaker(
  speakerValue: SpeakerValue,
  options: CreateSpeaker,
): SpeakerLanguageVoice {
  const projectStore = useProjectStore()

  const {
    voices,
    selectedGender,
    languages = ['', ...projectStore.languages],
    unsetDefualtLanguage,
  } = options

  const speakerLanguageVoice: SpeakerLanguageVoice = {}

  languages.forEach((lang) => {
    if (unsetDefualtLanguage && !lang) {
      speakerLanguageVoice[lang] = {}
    } else {
      const defaultVoice = getSpeakerDefaultVoice({
        speechVoiceModelList: voices,
        gender: selectedGender ?? '',
        language: lang,
        speaker: speakerValue,
      })

      speakerLanguageVoice[lang] = createSpeakerVoice({
        ...options,
        voice: defaultVoice ?? undefined,
      })
    }
  })

  return speakerLanguageVoice
}

export function createSpeakerVoice(
  options: CreateSpeakerVoice,
): SettledSpeakerVoice {
  const { clone, voice } = options

  return clone
    ? { clone: true }
    : {
        clone: false,
        style: voice?.voice_style ?? '',
        role: voice?.voice_role ?? '',
        model: voice?.model ?? '',
        name: voice?.name ?? '',
        language: voice?.language ?? '',
        gender: voice?.gender ?? '',
        selectedGender: options.selectedGender,
      }
}

/**
 * 指定一个speaker，获取config的默认语言，填充其他语言
 * @param speakerValue
 * @param config
 * @param options
 * @returns
 */
export function resolveFullLanguageSpeakerConfig<
  T extends SpeakerLanguageVoice,
>(
  speakerValue: keyof SpeakerCollection,
  config: T | undefined,
  options: Pick<CreateSpeaker, 'unsetDefualtLanguage' | 'voices'>,
): SpeakerLanguageVoice {
  const defaultVoice = config?.[''] as SpeakerVoice | undefined
  const speakerLanguageVoice = createSpeaker(speakerValue, {
    clone: false,
    voices: options.voices,
    selectedGender: (defaultVoice?.selectedGender ??
      defaultVoice?.gender ??
      Gender.Neutral) as Gender,
    unsetDefualtLanguage: options?.unsetDefualtLanguage,
  })

  speakerLanguageVoice[''] = cloneDeep(
    defaultVoice ?? speakerLanguageVoice['']!,
  )

  return speakerLanguageVoice
}

/**
 * https://confluence.mobvista.com/display/PLAB/Speaker#Speaker-resolveDefaultSpeaker
 * @param languages
 * @param options
 * @returns
 */
export async function resolveDefaultSpeaker(
  languages: Array<keyof SpeakerLanguageVoice>,
  options?: {
    type?: 'create'
    // 需求：当用户新增角色后，音色选项值为空值（初始值）
    unsetVoiceForDefaultLanguage?: boolean
    segmentVideoId?: SegmentCollection['id']
  },
): Promise<SpeakerValue> {
  const { unsetVoiceForDefaultLanguage = false, segmentVideoId = 0 } =
    options ?? {}

  if (options?.type !== 'create') {
    const s = getCustomSpeaker()[0]
    if (s) return s
  }

  const data = (await getSpeechVoiceList()).data.data ?? []
  const speaker = addCustomSpeaker(segmentVideoId)
  await addSpeakerVoice({
    speaker: speaker,
    segmentVideoId,
    languages: ['', ...languages],
    voiceList: data,
  })

  if (unsetVoiceForDefaultLanguage) {
    unsetDefaultLangSpeakerVoice({
      segmentVideoId,
      speakerValue: speaker,
    })
  }

  return speaker
}

/**
 * 不使用配音
 */
export function noDubbingSpeakerVoice(
  key: keyof ProjectConfig['speaker'],
  speaker: SpeakerValue,
  lang: keyof SpeakerLanguageVoice,
): void {
  const projectStore = useProjectStore()

  const voice = projectStore.configs.speaker[key]?.[speaker]?.[lang]

  if (!voice) {
    requredSpeakerVoice(arguments)
    return
  }

  const update = () => {
    updateSpeakerVoice({
      voice: undefined,
      segmentVideoId: key,
      speaker,
      lang,
      clone: false,
      autoSave: false,
    })
  }

  if (isSettledSpeakerVoice(voice)) {
    if (voice.clone) {
      update()
    } else {
      voice.model = ''
      voice.name = ''
    }
  } else {
    update()
  }
}
export function unsetSpeakerVoice(
  speaker: SpeakerLanguageVoice,
  lang: keyof SpeakerLanguageVoice,
): void {
  Vue.set(speaker, lang, {})
}

export function unsetDefaultLangSpeakerVoice(params: {
  segmentVideoId: keyof ProjectConfig['speaker']
  speakerValue: keyof SpeakerCollection
}): void {
  const { segmentVideoId, speakerValue } = params
  const projectStore = useProjectStore()
  const speaker = projectStore.configs.speaker[segmentVideoId]?.[speakerValue]
  if (speaker) {
    unsetSpeakerVoice(speaker, '')
  }
}

export function isSettledSpeaker(
  speaker: SpeakerValue,
  lang: keyof SpeakerLanguageVoice,
): boolean {
  const voice = resolveSpeakerVoice(speaker, lang)

  return voice ? isSettledSpeakerVoice(voice) : false
}

export function isSettledSpeakerVoice(
  speaker: Speaker,
): speaker is SettledSpeakerVoice {
  return !(Object.keys(speaker).length === 0)
}

export function isNoDubbingSpeaker(
  speaker: SpeakerValue,
  lang: keyof SpeakerLanguageVoice,
): boolean {
  const voice = resolveSpeakerVoice(speaker, lang)
  return voice ? isNoDubbingSpeakerVoice(voice) : false
}

export function isNoDubbingSpeakerVoice(
  speaker: Speaker,
): speaker is SpeakerVoice {
  if (isSettledSpeakerVoice(speaker) && speaker.clone === false) {
    return !speaker.model
  }

  return false
}

/**
 * https://confluence.mobvista.com/display/PLAB/Speaker#Speaker-speakerInfo
 */
export function resolveSpeakerVoiceLabel(
  speaker: SpeakerValue,
  lang: keyof SpeakerLanguageVoice,
  options?: {
    scopedKey?: keyof ProjectConfig['speaker']
    voiceList?: VoiceItem[]
  },
): TranslateResult {
  const voice = resolveSpeakerVoice(speaker, lang, options)

  // 空态
  if (!voice) {
    // requredSpeakerVoice(arguments)
    return SPEAKER_VOICE_UNSET_LABLE
  }

  if (isSpeakerUserUpload(voice)) {
    return (
      options?.voiceList?.find((item) => item.id === voice.voice_id)?.name ??
      SPEAKER_VOICE_UNSET_LABLE
    )
  }

  if (!isSettledSpeakerVoice(voice)) {
    return SPEAKER_VOICE_UNSET_LABLE
  }

  // 克隆
  if (voice.clone) {
    return SPEAKER_VOICE_CLONE_LABLE
  }

  // 「不使用配音」
  // 同：isNoDubbingSpeakerVoice
  if (!voice.model) {
    return SPEAKER_VOICE_UNUSE_LABLE
  }

  const titleList: string[] = []
  if (voice.name) {
    titleList.push(voice.name)
  }
  if (voice.role) {
    titleList.push(t(`basic.tone.role.${voice.role}`))
  }
  if (voice.style) {
    titleList.push(t(`basic.tone.style.${voice.style.replace(/-/g, '')}`))
  }

  return titleList.join('-')
}

export function resolveSpeakerVoice(
  speaker: SpeakerValue,
  lang: keyof SpeakerLanguageVoice,
  options?: {
    scopedKey?: keyof ProjectConfig['speaker']
  },
): Speaker | undefined {
  const { scopedKey } = options ?? {}
  const projectStore = useProjectStore()
  const replaceStore = useReplaceStore()

  let speakerItem: SpeakerCollection | undefined
  // 快速替换才通过 key 以及replaceStore.detailVideoId 去获取 speaker
  if (projectStore.isReplaceProject) {
    speakerItem =
      projectStore.configs.speaker[scopedKey ?? replaceStore.detailVideoId]
  } else {
    _.each(projectStore.configs.speaker, (item) => {
      if (item[speaker]) {
        speakerItem = item
      }
    })
  }

  const voice = speakerItem?.[speaker]?.[lang]

  return voice
}

export function resolveSpeakerValue(order: number): keyof SpeakerCollection {
  const count = order < 10 ? `0${order}` : order

  return `SPEAKER_${count}`
}

let _voiceListPromise: Promise<SpeechVoiceModel[]> | null = null
export function useVoiceListPromise(): Promise<SpeechVoiceModel[]> {
  if (!_voiceListPromise) {
    _voiceListPromise = retryPromise(() =>
      getSpeechVoiceList().then((res) => res.data.data ?? []),
    )
  }

  return _voiceListPromise
}

const speechVoiceList = ref<SpeechVoiceModel[]>([])
let speechVoiceListLoading = false
export function useVoiceList(): ComputedRef<SpeechVoiceModel[]> {
  if (!speechVoiceListLoading && speechVoiceList.value.length === 0) {
    speechVoiceListLoading = true
    useVoiceListPromise()
      .then((data) => {
        speechVoiceList.value = data
      })
      .finally(() => {
        speechVoiceListLoading = false
      })
  }

  return speechVoiceList as any
}

function getDeletedSpeakerVoiceIds(options?: {
  segments?: SegmentCollection[]
  languages?: string[]
}): VoiceItem['id'][] {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()
  const { segments = trackStore.segments, languages } = options ?? {}

  const activeSpeakers = getSpeakersBySegments(segments)

  const deletedVoiceIds: VoiceItem['id'][] = []

  Object.values(activeSpeakers).forEach((speaker) => {
    Object.values(speaker).forEach((langs) => {
      const langsValues = languages ?? Object.keys(langs)

      langsValues.forEach((lang) => {
        const voice = langs[lang]
        if (
          voice &&
          isSpeakerUserUpload(voice) &&
          !projectStore.voiceList.some((v) => v.id === voice.voice_id)
        ) {
          deletedVoiceIds.push(voice.voice_id)
        }
      })
    })
  })

  return deletedVoiceIds
}

// 检查部分音色已被删除
export function checkSpeakerVoiceDeleted(options?: {
  showTips?: boolean
  segments?: SegmentCollection[]
  languages?: string[]
}): boolean {
  const trackStore = useTrackStore()
  const {
    showTips = true,
    segments = trackStore.segments,
    languages,
  } = options ?? {}

  if (getDeletedSpeakerVoiceIds({ segments, languages }).length) {
    if (showTips) {
      Message.error(t('videoTemplate.addVoice.deteled'))
    }
    return false
  }
  return true
}

/** 把speaker格式化成 角色N */
export function getSpeakerLabel(speaker: string): string {
  const label = speaker.split('_')
  return `${t('videoTemplate.detail.table.action.speaker')} ${
    Number(label[1]) + 1
  }`
}

/** 更新快速克隆的元件为指定音色 */
export async function updateCloneVoices(
  cloneVoices: Array<{
    segment: SegmentText
    voiceId: VoiceItem['id']
  }>,
): Promise<void> {
  const projectStore = useProjectStore()
  const replaceStore = useReplaceStore()
  const speakers = cloneVoices.map(({ segment, voiceId }) => {
    return {
      voiceId: voiceId,
      segment: segment,
      speaker: resolveSpeakerVoice(segment.speaker, segment.lang),
    }
  })

  console.log('speakers', speakers)

  await projectStore.getVoiceList()

  // 去重
  _.uniqBy(speakers, 'speaker').forEach((item) => {
    console.log('speaker', item)

    updateSpeakerVoiceByLab({
      id: item.voiceId,
      segmentVideoId: replaceStore.detailVideoId,
      speaker: item.segment.speaker,
      language: item.segment.lang,
    })
  })
}
